import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import Layout from '@/components/Layout';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';
import {
  Wrench, Star, MapPin, Phone, Mail, Plus, MessageCircle, Clock,
  ShieldCheck, Search, Filter, Loader2, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SERVICE_TYPES = [
  { id: '',           label_en: 'All services',     label_ar: 'كل الخدمات' },
  { id: 'plumbing',   label_en: 'Plumbing',         label_ar: 'سباكة' },
  { id: 'electrical', label_en: 'Electrical',       label_ar: 'كهرباء' },
  { id: 'hvac',       label_en: 'HVAC / AC',        label_ar: 'تكييف' },
  { id: 'cleaning',   label_en: 'Cleaning',         label_ar: 'تنظيف' },
  { id: 'landscaping',label_en: 'Landscaping',      label_ar: 'تنسيق حدائق' },
  { id: 'security',   label_en: 'Security',         label_ar: 'أمن' },
  { id: 'general',    label_en: 'General handyman', label_ar: 'عامة' },
];

const Stars = ({ rating = 0, size = 12 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} className={i <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'} style={{ width: size, height: size }} />
    ))}
  </span>
);

export default function Marketplace() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [sort, setSort] = useState('rating');
  const [createOpen, setCreateOpen] = useState(false);
  const [reviewVendor, setReviewVendor] = useState(null);

  const fetchVendors = async () => {
    setLoading(true);
    const params = new URLSearchParams({ sort });
    if (serviceFilter) params.set('service_type', serviceFilter);
    if (cityFilter) params.set('city', cityFilter);
    try {
      const r = await axios.get(`${API}/marketplace/vendors?${params}`, { headers: authHeader() });
      setVendors(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchVendors(); /* eslint-disable-next-line */ }, [serviceFilter, cityFilter, sort]);

  const filtered = vendors.filter(v => !query || v.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Wrench className="h-5 w-5 text-orange-700" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'سوق مزوّدي الصيانة' : 'Maintenance Marketplace'}</h1>
              <p className="text-sm text-gray-500">{isAr ? 'دليل المقاولين والموردين الموثوقين — قيّم، اطلب، وتابع.' : 'Trusted vendor directory — rate, dispatch, and track.'}</p>
            </div>
          </div>
          <CreateVendorDialog open={createOpen} setOpen={setCreateOpen} onCreated={fetchVendors} isAr={isAr} />
        </div>

        {/* Filter strip */}
        <Card className="shadow-soft border-gray-100">
          <CardContent className="pt-5 pb-5">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-4 relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input value={query} onChange={e => setQuery(e.target.value)} placeholder={isAr ? 'ابحث باسم المزوّد' : 'Search vendor name'} className="ps-9" data-testid="vendor-search" />
              </div>
              <div className="md:col-span-3">
                <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm" data-testid="vendor-service-filter">
                  {SERVICE_TYPES.map(s => <option key={s.id} value={s.id}>{isAr ? s.label_ar : s.label_en}</option>)}
                </select>
              </div>
              <div className="md:col-span-3">
                <Input value={cityFilter} onChange={e => setCityFilter(e.target.value)} placeholder={isAr ? 'المدينة' : 'City'} data-testid="vendor-city-filter" />
              </div>
              <div className="md:col-span-2">
                <select value={sort} onChange={e => setSort(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm" data-testid="vendor-sort">
                  <option value="rating">{isAr ? 'الأعلى تقييماً' : 'Top rated'}</option>
                  <option value="jobs">{isAr ? 'الأكثر إنجازاً' : 'Most jobs'}</option>
                  <option value="newest">{isAr ? 'الأحدث' : 'Newest'}</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vendor grid */}
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-gray-500">
            <Filter className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm">{isAr ? 'لا يوجد مزوّدون يطابقون البحث.' : 'No vendors match your filters.'}</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(v => {
              const waUrl = v.phone ? `https://wa.me/${(v.phone || '').replace(/\D/g,'')}` : null;
              const telUrl = v.phone ? `tel:${v.phone}` : null;
              return (
                <Card key={v.id} className="shadow-soft border-gray-100 hover:shadow-hover transition-all" data-testid={`vendor-card-${v.id}`}>
                  <CardContent className="pt-5 pb-5">
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <h3 className="font-heading font-bold text-gray-900 text-base truncate">{v.name}</h3>
                          {v.is_verified && <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-100 rounded text-[10px] font-bold uppercase tracking-wider">{v.service_type}</span>
                          {v.response_sla_hours && (
                            <span className="text-[10px] text-gray-500 inline-flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{v.response_sla_hours}h SLA</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                      <Stars rating={v.avg_rating || 0} />
                      <span className="text-xs font-semibold text-gray-700">{(v.avg_rating || 0).toFixed(1)}</span>
                      <span className="text-[11px] text-gray-400">·  {v.review_count || 0} {isAr ? 'تقييم' : 'reviews'}</span>
                      {v.completed_jobs > 0 && <span className="text-[11px] text-gray-400">· {v.completed_jobs} {isAr ? 'وظيفة' : 'jobs'}</span>}
                    </div>

                    {v.description && (
                      <p className="text-xs text-gray-600 leading-relaxed mb-3 line-clamp-2">{v.description}</p>
                    )}

                    {v.coverage_cities && v.coverage_cities.length > 0 && (
                      <div className="flex items-center gap-1.5 mb-3 text-[11px] text-gray-500">
                        <MapPin className="h-3 w-3" />
                        {v.coverage_cities.slice(0,3).join(' · ')}
                      </div>
                    )}

                    {v.hourly_rate && (
                      <div className="text-xs text-gray-700 font-semibold mb-3">{v.hourly_rate} SAR / {isAr ? 'ساعة' : 'hour'}</div>
                    )}

                    <div className="flex items-center gap-1.5 pt-3 border-t border-gray-100">
                      {waUrl && (
                        <a href={waUrl} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 bg-[#25D366] hover:bg-[#1da851] text-white rounded-md text-xs font-semibold" data-testid={`vendor-wa-${v.id}`}>
                          <MessageCircle className="h-3 w-3" /> WhatsApp
                        </a>
                      )}
                      {telUrl && (
                        <a href={telUrl} className="inline-flex items-center justify-center w-9 h-9 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md" title={v.phone}>
                          <Phone className="h-3 w-3" />
                        </a>
                      )}
                      {v.email && (
                        <a href={`mailto:${v.email}`} className="inline-flex items-center justify-center w-9 h-9 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md" title={v.email}>
                          <Mail className="h-3 w-3" />
                        </a>
                      )}
                      <button onClick={() => setReviewVendor(v)} className="inline-flex items-center justify-center px-2 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-md text-xs font-semibold" data-testid={`vendor-review-${v.id}`}>
                        <Star className="h-3 w-3 me-1" /> {isAr ? 'قيّم' : 'Rate'}
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Review dialog */}
        {reviewVendor && (
          <ReviewDialog vendor={reviewVendor} onClose={() => setReviewVendor(null)} onPosted={() => { setReviewVendor(null); fetchVendors(); }} isAr={isAr} />
        )}

        <ModuleIntelligenceBlock module="vendors" testId="vendors-intel" />
      </div>
    </Layout>
  );
}

function CreateVendorDialog({ open, setOpen, onCreated, isAr }) {
  const [form, setForm] = useState({ name: '', service_type: 'plumbing', phone: '', email: '', description: '', response_sla_hours: 4, hourly_rate: '', coverage_cities: '', is_verified: false });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : null,
        coverage_cities: form.coverage_cities ? form.coverage_cities.split(',').map(s=>s.trim()).filter(Boolean) : [],
      };
      await axios.post(`${API}/vendors`, payload, { headers: authHeader() });
      setOpen(false); setForm({ name: '', service_type: 'plumbing', phone: '', email: '', description: '', response_sla_hours: 4, hourly_rate: '', coverage_cities: '', is_verified: false });
      onCreated();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gradient-primary text-white rounded-xl" data-testid="add-vendor-btn">
          <Plus className="h-4 w-4 me-1" />{isAr ? 'إضافة مزوّد' : 'Add vendor'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{isAr ? 'إضافة مزوّد جديد' : 'Add a new vendor'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><label className="text-xs font-medium text-gray-600">{isAr ? 'الاسم *' : 'Name *'}</label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">{isAr ? 'الخدمة' : 'Service'}</label>
              <select value={form.service_type} onChange={e=>setForm({...form,service_type:e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm">
                {SERVICE_TYPES.filter(s=>s.id).map(s=><option key={s.id} value={s.id}>{isAr ? s.label_ar : s.label_en}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium text-gray-600">{isAr ? 'الجوال *' : 'Phone *'}</label><Input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+9665..." required /></div>
          </div>
          <div><label className="text-xs font-medium text-gray-600">{isAr ? 'البريد' : 'Email'}</label><Input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
          <div><label className="text-xs font-medium text-gray-600">{isAr ? 'الوصف' : 'Description'}</label><textarea className="w-full border border-gray-200 rounded-md p-2 text-sm min-h-[60px]" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-gray-600">{isAr ? 'SLA (ساعة)' : 'SLA (hrs)'}</label><Input type="number" value={form.response_sla_hours} onChange={e=>setForm({...form,response_sla_hours:Number(e.target.value)})} /></div>
            <div><label className="text-xs font-medium text-gray-600">{isAr ? 'سعر الساعة' : 'Hourly rate'}</label><Input type="number" value={form.hourly_rate} onChange={e=>setForm({...form,hourly_rate:e.target.value})} /></div>
            <div className="flex items-end gap-1"><label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={form.is_verified} onChange={e=>setForm({...form,is_verified:e.target.checked})} />{isAr ? 'موثّق' : 'Verified'}</label></div>
          </div>
          <div><label className="text-xs font-medium text-gray-600">{isAr ? 'مدن التغطية (مفصولة بفاصلة)' : 'Coverage cities (comma separated)'}</label><Input value={form.coverage_cities} onChange={e=>setForm({...form,coverage_cities:e.target.value})} placeholder="Riyadh, Jeddah" /></div>
          <Button type="submit" disabled={submitting} className="w-full gradient-primary text-white">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isAr ? 'إضافة' : 'Add vendor')}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ vendor, onClose, onPosted, isAr }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/vendors/${vendor.id}/reviews`, { rating, comment }, { headers: authHeader() });
      onPosted();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={!!vendor} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{isAr ? `قيّم ${vendor.name}` : `Rate ${vendor.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-2 py-3">
            {[1,2,3,4,5].map(i => (
              <button key={i} onClick={() => setRating(i)} className="hover:scale-110 transition-transform" data-testid={`review-star-${i}`}>
                <Star className={`h-8 w-8 ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={isAr ? 'تعليقك (اختياري)...' : 'Your comment (optional)...'}
            className="w-full border border-gray-200 rounded-md p-3 text-sm min-h-[80px]"
            data-testid="review-comment"
          />
          <Button onClick={submit} disabled={submitting} className="w-full gradient-primary text-white" data-testid="review-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isAr ? 'أرسل التقييم' : 'Submit review')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
